/**
 * "Meine Einstellungen" (CC_11 Block 4, D-72) — the per-user GLOBAL settings
 * page. v1.5 carried one setting (isolated column resize). AP2·P3-4 (Run C) adds
 * the KI-Systemprompt slots: the user's own prompt and — for owner/admin — the
 * workspace guardrails. Both save via PUT /ki/prompt and surface the
 * non-blocking author-time consistency warnings (B3).
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type { KiWarning, UserSettings } from "../api";

/** One prompt slot: textarea + save + consistency warnings. */
const KiPromptEditor = ({
  title,
  hint,
  ws,
  level,
  refId,
}: {
  title: string;
  hint: string;
  ws: string;
  level: "user" | "workspace";
  refId: string;
}) => {
  const [value, setValue] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [warnings, setWarnings] = useState<KiWarning[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    ryfeApi
      .getKiPrompt(ws, level, refId)
      .then((r) => {
        if (!cancelled) {
          setValue(r.ki_system_prompt || "");
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ws, level, refId]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const r = await ryfeApi.setKiPrompt(ws, {
        level,
        ref_id: refId,
        ki_system_prompt: value,
      });
      setWarnings(r.warnings || []);
      setSaved(true);
    } catch {
      setError("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ryfe-panel">
      <h2 className="ryfe-panel__title">{title}</h2>
      <p className="ryfe-muted" style={{ padding: 0, marginTop: 0 }}>
        {hint}
      </p>
      <textarea
        className="ryfe-field"
        rows={5}
        value={value}
        disabled={!loaded || saving}
        placeholder="Systemprompt für diese Ebene …"
        data-testid={`ki-prompt-${level}`}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className="ryfe-toolbar" style={{ marginTop: "0.5rem" }}>
        <button
          className="ryfe-btn ryfe-btn--primary"
          disabled={!loaded || saving}
          onClick={save}
        >
          Speichern
        </button>
        {saved && !warnings.length ? (
          <span className="ryfe-muted" style={{ padding: 0 }}>
            Gespeichert.
          </span>
        ) : null}
        {error ? <span className="ryfe-auth__error">{error}</span> : null}
      </div>
      {warnings.length > 0 ? (
        <div className="ryfe-notice" data-testid={`ki-warnings-${level}`}>
          <strong>Mögliche Widersprüche</strong> (nicht blockierend — du
          entscheidest):
          <ul style={{ margin: "0.4rem 0 0" }}>
            {warnings.map((w, i) => (
              <li key={i}>
                <code>{w.level}</code> · {w.severity}: {w.summary}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export const RyfeSettings = () => {
  const { user, workspace } = useRyfe();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ryfeApi
      .getSettings()
      .then((r) => {
        if (!cancelled) {
          setSettings(r.settings);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSettings({ columnResizeIsolated: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleIsolated = async () => {
    if (!settings || saving) {
      return;
    }
    const next = !settings.columnResizeIsolated;
    setSaving(true);
    try {
      const r = await ryfeApi.patchSettings({ columnResizeIsolated: next });
      setSettings(r.settings);
    } catch {
      /* keep the previous value — nothing was saved */
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="ryfe-muted">Lädt…</div>;
  }

  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin";

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">Meine Einstellungen</h1>

      {user && workspace ? (
        <KiPromptEditor
          title="Mein KI-Systemprompt"
          hint="Gilt als deine Persona-Ebene im KI-Chat (wird empty-safe mit den anderen Ebenen komponiert)."
          ws={workspace.id}
          level="user"
          refId={user.id}
        />
      ) : null}

      {isAdmin && workspace ? (
        <KiPromptEditor
          title="Workspace-Guardrails (bindend)"
          hint="Verbindliche Leitplanken für alle ws-gebundenen KI-Ordner dieses Workspace. Stehen im komponierten Prompt ganz oben."
          ws={workspace.id}
          level="workspace"
          refId={workspace.id}
        />
      ) : null}

      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">Tabellen</h2>
        <label className="ryfe-check">
          <input
            type="checkbox"
            data-testid="setting-isolated"
            checked={settings.columnResizeIsolated}
            disabled={saving}
            onChange={toggleIsolated}
          />
          Spaltenbreite isoliert ändern (nur die gezogene Spalte)
        </label>
        <p className="ryfe-muted" style={{ padding: 0 }}>
          Gilt global für alle Tabellen dieses Kontos. Das Merken der Breite pro
          Tabelle folgt in einer späteren Version.
        </p>
      </div>
    </div>
  );
};
